import { createFormContext } from "@mantine/form";
import dayjs from "dayjs";
import { StrictUndef } from "shared/api/httpClient";
import { arr } from "shared/utils/array";
import { fail, unique } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { maybe } from "shared/utils/maybe";
import { zx } from "shared/utils/zx";
import {
    IpawsCapMessageDetailsFragment,
    IpawsCapMessageInfoCategory,
    IpawsCapMessageInfoCertainty,
    IpawsCapMessageInfoSeverity,
    IpawsCapMessageInfoTzCode,
    IpawsCapMessageInfoUrgency,
    IpawsCapMessageScope,
} from "src/gql/graphql";
import {
    IpawsCapMessageInfoCategorySchema,
    IpawsCapMessageInfoCertaintySchema,
    IpawsCapMessageInfoSeveritySchema,
    IpawsCapMessageInfoTzCodeSchema,
    IpawsCapMessageInfoUrgencySchema,
    IpawsChannel,
    IpawsChannelSchema,
    IpawsEventCodeSchema,
    IpawsMessageDataInput,
    IpawsMessageDataInputSchema,
    IpawsMessageDataStatus,
    IpawsMessageDataStatusSchema,
    Language,
    LanguageSchema,
} from "src/gql/zod";
import { match, P } from "ts-pattern";
import z from "zod";

const ipawsAlertFormAreaStateSchema = z.object({
    name: z.string(),
    codes: z.string().array(),
    features: zx.geo.feature<GeoJSON.Polygon>(zx.geo.polygon()).array(),
    circles: z
        .object({
            point: zx.geo.point(),
            radius: z.number(),
        })
        .array(),
    meta: z
        .object({
            bounds: zx.lngLatBounds().optional(),
            search: z.string().default(""),
        })
        .default({}),
});

const ipawsAlertFormStateSchema = z.object({
    customerId: z.string().uuid().optional(),
    certId: z.string().uuid().optional(),
    tz: IpawsCapMessageInfoTzCodeSchema.optional(),
    channels: IpawsChannelSchema.array().default([]),
    status: IpawsMessageDataStatusSchema.default(IpawsMessageDataStatus.Actual),
    note: z.string().optional(),
    eventCode: IpawsEventCodeSchema.optional(),
    durationMinutes: z.number().int().min(15, { message: "Must be greater than 15 minutes" }).default(60),
    restrictions: z.string().default(""),
    cogRecipients: z.string().array().default([]),
    areas: ipawsAlertFormAreaStateSchema.array().default([{ name: "Area 1", codes: [], features: [], circles: [] }]),
    category: IpawsCapMessageInfoCategorySchema.optional(),
    severity: IpawsCapMessageInfoSeveritySchema.optional(),
    urgency: IpawsCapMessageInfoUrgencySchema.optional(),
    certainty: IpawsCapMessageInfoCertaintySchema.optional(),
    contents: z
        .object({
            language: LanguageSchema,
            headline: z.string(),
            description: z.string(),
            instructions: z.string(),
            contactInfo: z.string(),
            wea: z.object({
                shortText: z.string(),
                longText: z.string(),
            }),
            easAudioFileId: z.string().uuid().optional(),
        })
        .array()
        .default([
            {
                language: Language.English,
                contactInfo: "",
                description: "",
                headline: "",
                instructions: "",
                wea: {
                    shortText: "",
                    longText: "",
                },
            },
        ]),
    meta: z
        .object({
            step: z.number().min(0).max(3).default(0),
            mode: z
                .union([
                    z.literal("new"),
                    z.object({ type: z.literal("edit"), draftId: z.string() }),
                    z.object({ type: z.literal("update"), alertId: z.string() }),
                ])
                .default("new"),
            allowedEventCodes: z.string().array().default([]),
            allowedGeocodes: z.string().array().default([]),
        })
        .default({}),
});

type IpawsAlertFormState = z.infer<typeof ipawsAlertFormStateSchema>;

export const [IpawsAlertFormProvider, useIpawsAlertFormContext, useIpawsAlertForm] = createFormContext<IpawsAlertFormState>();

export function createDefaultIpawsAlertFormState() {
    return ipawsAlertFormStateSchema.parse({
        tz:
            match(dayjs.tz.guess())
                .with("America/Glace_Bay", () => IpawsCapMessageInfoTzCode.Akst)
                .with("America/New_York", () => IpawsCapMessageInfoTzCode.Est)
                .with("America/Chicago", () => IpawsCapMessageInfoTzCode.Cst)
                .with("America/Denver", () => IpawsCapMessageInfoTzCode.Mst)
                .with("America/Los_Angeles", () => IpawsCapMessageInfoTzCode.Pst)
                .with("Pacific/Honolulu", () => IpawsCapMessageInfoTzCode.Pst)
                .with("America/Anchorage", () => IpawsCapMessageInfoTzCode.Akst)
                .with("Pacific/Pago_Pago", () => IpawsCapMessageInfoTzCode.Sst)
                .with("Pacific/Guam", () => IpawsCapMessageInfoTzCode.Chst)
                .run() ?? logger.warn("Failed to match timezone guess, " + dayjs.tz.guess()),
    }); // initialize with defaults
}

export function createIpawsAlertFormState(
    frg: StrictUndef<IpawsCapMessageDetailsFragment>,
    ctx: {
        certId: string;
        customerId: string;
        durationMinutes: number | undefined;
        mode: ["edit", string] | ["update", string] | "new";
    },
): IpawsAlertFormState {
    const info = frg.infos.find((it) => it.language == "en-US") ?? fail("Failed to find en-US info.");
    const { tz } = createDefaultIpawsAlertFormState();

    return {
        certId: ctx.certId,
        customerId: ctx.customerId,
        tz,
        areas: info.areas.map((a) => ({
            circles: a.circles.map((c) => ({ ...c, point: zx.geo.point().parse(c.point) })),
            codes: a.geocodes,
            features: a.polygons.map((p) => ({ type: "Feature", geometry: zx.geo.polygon().parse(p), properties: {} })),
            name: a.description,
            meta: {
                search: "",
            },
        })),
        channels: arr(Object.values(IpawsChannel))
            .except(IpawsChannel.Capexch, ...info.blockedChannels)
            .appendIf(frg.addresses.length > 0, IpawsChannel.Capexch) // only check "CAPEXCH" if there's a cog recipient
            .take(),
        cogRecipients: frg.addresses.map((it) => it.value),
        contents: frg.infos.map((info) => ({
            language: info.language == "en-US" ? Language.English : Language.Spanish,
            contactInfo: info.contactInfo ?? "",
            description: info.description ?? "",
            headline: info.headline ?? "",
            instructions: info.instruction ?? "",
            wea: {
                shortText: info.cmamText ?? "",
                longText: info.cmamLongText ?? "",
            },
            easAudioFileId: maybe(info.resources.find((r) => r.mimeType == "audio/x-ipaws-audio-mp3")?.uri)?.take((uri) =>
                uri.split("/").at(-1),
            ),
        })),
        durationMinutes: ctx.durationMinutes ?? 60,
        category: info.categories.at(0) ?? IpawsCapMessageInfoCategory.Other,
        certainty: info.certainty,
        severity: info.severity,
        urgency: info.urgency,
        eventCode: maybe(info.eventCodes.at(0))?.take(IpawsEventCodeSchema.parse) ?? fail("Expected at least 1 event code."),
        note: frg.note ?? "",
        restrictions: frg.restriction ?? "",
        status: maybe(frg.status)?.take(IpawsMessageDataStatusSchema.parse) ?? IpawsMessageDataStatus.Actual,
        meta: {
            step: 0,
            mode: match(ctx.mode)
                .with("new", () => <const>"new")
                .with(["edit", P.string], ([_, draftId]) => ({ type: <const>"edit", draftId }))
                .with(["update", P.string], ([_, alertId]) => ({ type: <const>"update", alertId }))
                .exhaustive(),
            allowedEventCodes: [],
            allowedGeocodes: [],
        },
    } satisfies IpawsAlertFormState;
}

export const ipawsAlertFormSteppedStateSchema = ipawsAlertFormStateSchema.superRefine((data, ctx) => {
    if (!data.certId) ctx.addIssue({ code: "custom", path: ["certId"], message: "Required." });
    if (data.channels.filter((ch) => ch != IpawsChannel.Public).length == 0)
        ctx.addIssue({ code: "custom", path: ["channels"], message: "At least one channel is required." });
    if (data.meta.allowedGeocodes.length == 0 && data.channels.length > 0)
        ctx.addIssue({ code: "custom", path: ["channels"], message: "There are no FIPS codes allowed for this channel selection." });
    if (data.meta.allowedEventCodes.length == 0 && data.channels.length > 0)
        ctx.addIssue({ code: "custom", path: ["channels"], message: "There are no event codes allowed for this channel selection." });

    if (data.meta.step > 0) {
        if (!data.eventCode) ctx.addIssue({ code: "custom", path: ["eventCode"], message: "Required" });
        if (!data.tz) ctx.addIssue({ code: "custom", path: ["tz"], message: "Required" });

        const maxDurationMinutes = data.channels.includes(IpawsChannel.Cmas) ? 24 * 60 : 99.5 * 60;
        if (data.durationMinutes > maxDurationMinutes) {
            ctx.addIssue({
                code: "custom",
                path: ["durationMinutes"],
                message: data.channels.includes(IpawsChannel.Cmas)
                    ? "WEA alert expiration cannot exceed 24 hours."
                    : "Alert expiration cannot exceed 99.5 hours.",
            });
        }

        if (data.channels.includes(IpawsChannel.Capexch)) {
            if (data.cogRecipients.length == 0) ctx.addIssue({ code: "custom", path: ["cogRecipients"], message: "Required" });
        }

        data.cogRecipients.forEach((cog) => {
            if (!Number.isInteger(Number.parseFloat(cog)))
                ctx.addIssue({ code: "custom", path: ["cogRecipients"], message: "Only integer numbers are permitted." });
        });
    }

    if (data.meta.step > 1) {
        data.areas.forEach((area, i) => {
            if (area.codes.length == 0)
                ctx.addIssue({ code: "custom", path: ["areas", i, "codes"], message: "At least one FIPS code is required." });
            if (area.name.trim() == "") ctx.addIssue({ code: "custom", path: ["areas", i, "name"], message: "Required." });
        });
    }

    if (data.meta.step > 2) {
        if (!data.category) ctx.addIssue({ code: "custom", path: ["category"], message: "Required." });
        if (!data.severity) ctx.addIssue({ code: "custom", path: ["severity"], message: "Required." });
        if (!data.urgency) ctx.addIssue({ code: "custom", path: ["urgency"], message: "Required." });
        if (!data.certainty) ctx.addIssue({ code: "custom", path: ["certainty"], message: "Required." });

        data.contents.forEach((content, i) => {
            if (content.headline.trim() == "") ctx.addIssue({ code: "custom", path: ["contents", i, "headline"], message: "Required." });
            if (content.description.trim() == "")
                ctx.addIssue({ code: "custom", path: ["contents", i, "description"], message: "Required." });

            if (data.channels.includes(IpawsChannel.Eas)) {
                if (!content.easAudioFileId)
                    ctx.addIssue({ code: "custom", path: ["contents", i, "easAudioFileId"], message: "Required." });
            }

            if (data.channels.includes(IpawsChannel.Cmas)) {
                if (!content.wea?.shortText.trim())
                    ctx.addIssue({ code: "custom", path: ["contents", i, "wea", "shortText"], message: "Required" });
                if (!content.wea?.longText.trim())
                    ctx.addIssue({ code: "custom", path: ["contents", i, "wea", "longText"], message: "Required" });

                if ((content.wea?.shortText.length ?? 0) > 90)
                    ctx.addIssue({ code: "custom", path: ["contents", i, "wea.shortText"], message: "Cannot exceed 90 characters" });
                if ((content.wea?.longText.length ?? 0) > 360)
                    ctx.addIssue({ code: "custom", path: ["contents", i, "wea.longText"], message: "Cannot exceed 360 characters" });

                if (!!data.urgency && ![IpawsCapMessageInfoUrgency.Immediate, IpawsCapMessageInfoUrgency.Expected].includes(data.urgency))
                    ctx.addIssue({ code: "custom", path: ["urgency"], message: "Urgency must be Immediate or Expected for WEA" });
                if (!!data.severity && ![IpawsCapMessageInfoSeverity.Extreme, IpawsCapMessageInfoSeverity.Severe].includes(data.severity))
                    ctx.addIssue({ code: "custom", path: ["severity"], message: "Severity must be Extreme or Severe for WEA" });
                if (
                    !!data.certainty &&
                    ![IpawsCapMessageInfoCertainty.Observed, IpawsCapMessageInfoCertainty.Likely].includes(data.certainty)
                )
                    ctx.addIssue({ code: "custom", path: ["certainty"], message: "Certainty must be Observed or Likely for WEA" });
            }
        });
    }
});

export const ipawsAlertFormStateSchemaToMessageDataInput = ipawsAlertFormStateSchema
    .transform((it) => {
        return {
            ...it,
            areas: it.areas.map((area) => ({
                ...area,
                polygons: area.features.map((f) => JSON.stringify(f.geometry)),
                circles: area.circles.map((c) => ({ ...c, point: JSON.stringify(c.point) })),
            })),
            channels: it.channels.concat([IpawsChannel.Public, IpawsChannel.Capexch]).filter(unique()),
            contents: it.contents.map((content) => ({
                ...content,
                wea: it.channels.includes(IpawsChannel.Cmas) ? content.wea : undefined,
            })),
            restrictions: maybe(it.restrictions)?.takeIf(() => it.channels.includes(IpawsChannel.Capexch)),
            category: it.category ?? fail("Expected category to be set"),
            certainty: it.certainty ?? fail("Expected certainty to be set"),
            urgency: it.urgency ?? fail("Expected urgency to be set"),
            severity: it.severity ?? fail("Expected severity to be set"),
            certId: it.certId ?? fail("Expected certId to be set"),
            cogRecipients:
                maybe(it.cogRecipients.map((it) => Number.parseInt(it))).takeIf(() => it.channels.includes(IpawsChannel.Capexch)) ?? [],
            customerId: it.customerId ?? fail("Expected customerId to be set"),
            eventCode: it.eventCode ?? fail("Expected eventCode to be set"),
            scope: arr(it.channels).congruentTo([IpawsChannel.Capexch]) ? IpawsCapMessageScope.Private : IpawsCapMessageScope.Public,
            status: it.status ?? fail("Expected status to be set"),
            tz: it.tz ?? fail("Expected tz to be set"),
        } satisfies IpawsMessageDataInput;
    })
    .pipe(IpawsMessageDataInputSchema());
